import { Suspense, lazy, useState } from 'react';
import type { ApiClient } from '../api';
import { BRAND_META, BRAND_ORDER } from '../brand';
import { Feed } from '../components/Feed';
import { Sparkline } from '../components/Sparkline';
import { TopTable, deeplinkEntityFor } from '../components/TopTable';
import { compact, isZeroMoney, money, moneyTotals } from '../format';
import { bucketise, usePoll } from '../hooks';
import type {
  BrandName,
  FeedEvent,
  GeoResponse,
  Me,
  SeriesPoint,
  Summary,
  TopRow,
} from '../types';

import { KpiGrid } from '../components/KpiGrid';
import { EmptyPanel } from '../components/EmptyPanel';
import { AppDownloads } from '../components/AppDownloads';
import { InPersonEvents } from '../components/InPersonEvents';

const GeoMap = lazy(() => import('../components/GeoMap').then((m) => ({ default: m.GeoMap })));

const ACTIVITY_EVENTS = [
  'click.recorded',
  'conversion.recorded',
  'transaction.recorded',
  'member.joined',
];

/**
 * The group view (§2.1.6): combined revenue, the cross-brand activity map
 * with the live event feed beside it, per-brand summaries, top performers
 * and top regions. Monitoring only; every row that has an admin record
 * behind it deep-links out.
 */
/** Map windows: sparse early traffic needs weeks to clear the privacy
 *  threshold; the threshold itself never moves. */
const GEO_WINDOWS = [
  { hours: 24, label: '24h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
] as const;

export function GroupView({
  api,
  me,
  mapStyleUrl,
  onDeepLink,
  onAsk,
}: {
  api: ApiClient;
  me: Me;
  mapStyleUrl: string;
  onDeepLink: (brand: BrandName, entity: string, id: string | null) => void;
  onAsk: (prompt: string) => void;
}) {
  const [geoHours, setGeoHours] = useState<number>(24);
  const summary = usePoll<Summary>(() => api.get('/api/dashboard/summary'), 30_000, ['summary']);
  const geo = usePoll<GeoResponse>(() => api.get(`/api/geo?hours=${geoHours}`), 60_000, ['geo', geoHours]);
  const feed = usePoll<{ events: FeedEvent[] }>(() => api.get('/api/feed?limit=40'), 15_000, ['feed']);
  const top = usePoll<{ rows: TopRow[] }>(() => api.get('/api/dashboard/top'), 120_000, ['top']);
  const series = usePoll<{ points: SeriesPoint[] }>(
    () => api.get('/api/dashboard/series?hours=24'),
    60_000,
    ['series'],
  );

  if (summary.error) {
    return <div className="load-error">The read model is unreachable: {summary.error.message}</div>;
  }

  const brands = BRAND_ORDER.filter((b) => me.brands.includes(b));

  return (
    <>
      <div className="cc-hero">
      <div className="grid map-row">
        <div className="card map-card">
          <div className="map-overlay-head">
            <span className="card-title">
              Activity, last {GEO_WINDOWS.find((w) => w.hours === geoHours)?.label ?? `${geoHours}h`}
            </span>
            <span className="map-window" role="tablist" aria-label="Map time window">
              {GEO_WINDOWS.map((w) => (
                <button
                  key={w.hours}
                  role="tab"
                  aria-selected={geoHours === w.hours}
                  onClick={() => setGeoHours(w.hours)}
                >
                  {w.label}
                </button>
              ))}
            </span>
          </div>
          <Suspense fallback={<div className="map-container" />}>
            <GeoMap
              api={api}
              cells={geo.data?.cells ?? []}
              styleUrl={mapStyleUrl}
              suppressed={geo.data?.suppressed}
              subThreshold={geo.data?.subThreshold ?? []}
              marks={geo.data?.marks ?? []}
              threshold={geo.data?.suppressionThreshold ?? 5}
              hours={geo.data?.hours ?? 24}
            />
          </Suspense>
          <span className="map-note">
            A place shows once it has {geo.data?.suppressionThreshold ?? 5} of the same kind of event; fewer sit at
            the country's centre, never a fine location
            {geo.data && geo.data.suppressed.events > 0
              ? ` · ${geo.data.suppressed.events} ${geo.data.suppressed.events === 1 ? 'event' : 'events'} currently below it`
              : ''}
          </span>
          <span className="map-attrib">
            © <a href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> ·{' '}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OSM</a>
          </span>
        </div>
        <div className="card">
          <div className="card-head">
            <span className="card-title">
              <span className="live-pip" aria-hidden />
              Live events
            </span>
            <span className="card-note">newest first</span>
          </div>
          <Feed
            api={api}
            events={feed.data?.events ?? null}
            onDeepLink={(e) => {
              const [entity, id] = e.offer_id
                ? ['offer', e.offer_id]
                : e.creator_id
                  ? ['creator', e.creator_id]
                  : e.merchant_id
                    ? ['merchant', e.merchant_id]
                    : ['advertiser', e.advertiser_id];
              onDeepLink(e.brand, entity!, id ?? null);
            }}
          />
        </div>
      </div>
      </div>

      {/* The KPI row sits UNDER the atlas (Matthew, 4 September 2026:
        * move the atlas above Revenue and Active brands). It moved out
        * of App so the ordering lives beside the thing it orders. */}
      <KpiGrid summary={summary.data ?? null} brands={brands} />

      {/* In-person events (18 October 2026): shows only when one ran in the last 30 days. */}
      <InPersonEvents api={api} />

      {/* App downloads (24 October 2026): every app's store figures. */}
      <AppDownloads api={api} />

      {/* The mock's three analysis panels. Framed and honest rather than
        * hidden or invented (Matthew, 4 September 2026), directly under
        * the hero. Each states what it is waiting for. */}
      <div className="cc-panel-row">
        <EmptyPanel
          title="Opportunity radar"
          reason="No opportunity source connected. This needs the CRM read model, which the dashboard does not read yet."
        />
        <EmptyPanel
          title="Risk overview"
          reason="Risk is not yet defined. Nothing in the platform currently scores or ranks risk, so there is nothing honest to show."
        />
        <EmptyPanel
          title="Today's schedule"
          reason="Needs a calendar connection. Nothing in the platform holds the day's commitments yet."
        />
      </div>

      <div className="grid cards-3">
        {brands.map((b) => {
          const today = summary.data?.today[b];
          const activity = bucketise(
            (series.data?.points ?? [])
              .filter((p) => p.brand === b && ACTIVITY_EVENTS.includes(p.event_type))
              .map((p) => ({ bucket_start: p.bucket_start, value: p.event_count })),
            24,
            24,
          );
          const meta = BRAND_META[b];
          return (
            <div key={b} className={`card brand-card${today?.instrumented === false ? ' pending-card' : ''}`}>
              <div className="brand-name">
                <span className="swatch" style={{ ['--series' as string]: meta.series, background: meta.series }} aria-hidden />
                {meta.label}
                {(me.role === 'owner' || me.role === 'operator') && today?.instrumented && (
                  <button className="deeplink" style={{ marginLeft: 'auto' }} onClick={() => onDeepLink(b, 'home', null)}>
                    Open admin →
                  </button>
                )}
              </div>
              {today?.instrumented === false ? (
                <p style={{ color: 'var(--ink-2)', fontSize: 13 }}>
                  Not instrumented yet. Events land here once milestone 0 ships; the tiles fill on their own.
                </p>
              ) : (
                <>
                  <div className={today && isZeroMoney(today.revenue) ? 'hero value-empty' : 'hero'}>
                    {today ? moneyTotals(today.revenue) : '…'}
                  </div>
                  <div className="hero-label">
                    {today?.revenue === null ? 'revenue hidden for your role · ' : ''}revenue today
                  </div>
                  {today &&
                    isZeroMoney(today.revenue) &&
                    today.newUsers === 0 &&
                    today.transactionCount === 0 && (
                      <div className="tile-note">{meta.emptyReason}</div>
                    )}
                  {(() => {
                    // Goal line (brand/goals.json): month to date against
                    // the monthly revenue target, drawn only when a real
                    // target exists and revenue is visible to this role.
                    const goal = summary.data?.goals?.[b];
                    const mtdRevenue = summary.data?.mtd[b]?.revenue;
                    if (!goal?.monthlyRevenueMinor || !mtdRevenue) return null;
                    const current = mtdRevenue[goal.revenueCurrency] ?? 0;
                    const pct = Math.min(100, (current / goal.monthlyRevenueMinor) * 100);
                    return (
                      <div className="goal-line">
                        <div className="goal-bar">
                          <div
                            className="goal-fill"
                            style={{ width: `${pct}%`, background: meta.series }}
                          />
                        </div>
                        <span className="goal-text">
                          {money(current, goal.revenueCurrency)} of{' '}
                          {money(goal.monthlyRevenueMinor, goal.revenueCurrency)} monthly goal ·{' '}
                          {Math.round(pct)}%
                        </span>
                      </div>
                    );
                  })()}
                  <Sparkline points={activity} color={meta.series} />
                  <div className="minis">
                    <div className="mini">
                      <div className="n">{today ? compact(today.newUsers) : '·'}</div>
                      <div className="l">{meta.usersLabel}</div>
                    </div>
                    <div className="mini">
                      <div className="n">{today ? compact(today.transactionCount) : '·'}</div>
                      <div className="l">{meta.txLabel}</div>
                    </div>
                    <div className="mini">
                      <div className="n">{today ? moneyTotals(today.payoutValue) : '·'}</div>
                      <div className="l">Payouts</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <h2 className="section-title">Top performers</h2>
      <div className="grid cards-2">
        {['offer', 'creator', 'merchant', 'advertiser'].map((entityType) => (
          <TopTable
            key={entityType}
            rows={top.data?.rows ?? []}
            entityType={entityType}
            me={me}
            onDeepLink={(row) => {
              const entity = deeplinkEntityFor(row.entity_type);
              if (entity) onDeepLink(row.brand, entity, row.entity_id);
            }}
          />
        ))}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Top regions</span>
            <span className="card-note">
              last {GEO_WINDOWS.find((w) => w.hours === geoHours)?.label ?? `${geoHours}h`} · aggregate
            </span>
          </div>
          {geo.data && geo.data.regions.length === 0 && (
            <div className="empty">Nothing above the suppression threshold in this window yet.</div>
          )}
          <div className="table-wrap">
            {geo.data && geo.data.regions.length > 0 && (
              <table className="data">
                <thead>
                  <tr>
                    <th>Place</th>
                    <th className="num">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {geo.data.regions.map((r) => (
                    <tr key={`${r.country}-${r.city}`}>
                      <td>{[r.city, r.country].filter(Boolean).join(', ')}</td>
                      <td className="num">{compact(r.event_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
